import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Typography,
  Box,
  Paper,
  Grid,
  Divider,
  Alert,
  IconButton,
  Tooltip,
  Breadcrumbs,
  Card,
  CardContent,
  LinearProgress,
  InputAdornment,
  Stack,
  Chip,
} from "@mui/material";
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Title as TitleIcon,
  Description as DescriptionIcon,
  Clear as ClearIcon,
  CategoryOutlined as CategoryIcon,
  Help as HelpIcon,
} from "@mui/icons-material";
import {
  fetchPodcastTypeById,
  createPodcastType,
  updatePodcastType,
  PodcastType,
} from "../../services/api";

type PodcastTypeFormData = Omit<PodcastType, "id">;

const PodcastTypeForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEditMode = !!id;

  const [formData, setFormData] = useState<PodcastTypeFormData>({
    title: "",
    description: "",
    active: true,
  });

  const [errors, setErrors] = useState<Partial<PodcastTypeFormData>>({});
  const [formTouched, setFormTouched] = useState<boolean>(false);
  const [charCount, setCharCount] = useState({
    title: 0,
    description: 0,
  });

  const {
    data: podcastTypeItem,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["podcastType", id],
    queryFn: () => fetchPodcastTypeById(Number(id)),
    enabled: isEditMode,
  });

  useEffect(() => {
    if (podcastTypeItem) {
      setFormData(podcastTypeItem);
      setCharCount({
        title: podcastTypeItem.title.length,
        description: podcastTypeItem.description.length,
      });
    }
  }, [podcastTypeItem]);

  const mutation = useMutation<
    PodcastType,
    Error,
    PodcastTypeFormData | PodcastType
  >({
    mutationFn: (data) => {
      if (isEditMode) {
        return updatePodcastType({ ...data, id: Number(id) });
      }
      return createPodcastType(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["podcastTypes"] });
      navigate("/podcast-types");
    },
  });

  const validateForm = (): boolean => {
    const newErrors: Partial<PodcastTypeFormData> = {};
    if (!formData.title.trim()) newErrors.title = "Title is required";
    if (formData.title.length > 100)
      newErrors.title = "Title is too long (maximum 100 characters)";

    if (!formData.description.trim())
      newErrors.description = "Description is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      mutation.mutate(formData);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === "checkbox" ? checked : value;

    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));

    // Update character count
    if (type === "text" || type === "textarea") {
      setCharCount((prev) => ({
        ...prev,
        [name]: value.length,
      }));
    }

    // Mark form as touched
    if (!formTouched) {
      setFormTouched(true);
    }

    // Clear error when user types
    if (errors[name as keyof PodcastTypeFormData]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleCancel = () => {
    if (formTouched) {
      if (
        window.confirm(
          "You have unsaved changes. Are you sure you want to leave?"
        )
      ) {
        navigate("/podcast-types");
      }
    } else {
      navigate("/podcast-types");
    }
  };

  // Reset form to original data
  const handleReset = () => {
    if (isEditMode && podcastTypeItem) {
      setFormData(podcastTypeItem);
      setCharCount({
        title: podcastTypeItem.title.length,
        description: podcastTypeItem.description.length,
      });
    } else {
      setFormData({
        title: "",
        description: "",
        active: true,
      });
      setCharCount({
        title: 0,
        description: 0,
      });
    }
    setErrors({});
  };

  if (isLoading) {
    return (
      <Box sx={{ width: "100%", my: 4 }}>
        <LinearProgress />
        <Typography variant="subtitle1" sx={{ mt: 2, textAlign: "center" }}>
          Loading podcast type...
        </Typography>
      </Box>
    );
  }

  if (isError && error) {
    return (
      <Alert
        severity="error"
        sx={{ mt: 2 }}
        action={
          <Button
            color="inherit"
            size="small"
            onClick={() => navigate("/podcast-types")}
          >
            Back to Podcast Types
          </Button>
        }
      >
        Error loading podcast type: {error.message}
      </Alert>
    );
  }

  return (
    <Box sx={{ mb: 4 }}>
      {/* Breadcrumb Navigation */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          to="/podcast-types"
          style={{ textDecoration: "none", color: "inherit" }}
        >
          Podcast Types
        </Link>
        <Typography color="text.primary">
          {isEditMode ? `Edit: ${formData.title}` : "Create Podcast Type"}
        </Typography>
      </Breadcrumbs>

      {/* Form Header */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          bgcolor: "background.paper",
          borderRadius: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <IconButton sx={{ mr: 1 }} onClick={handleCancel}>
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <CategoryIcon sx={{ mr: 1, color: "primary.main" }} />
            <Typography variant="h5" component="h1">
              {isEditMode ? "Edit Podcast Type" : "Create Podcast Type"}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            onClick={handleReset}
            disabled={mutation.isPending}
          >
            Reset
          </Button>
          <Button
            variant="outlined"
            onClick={handleCancel}
            disabled={mutation.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSubmit}
            disabled={mutation.isPending}
            color="primary"
          >
            {mutation.isPending ? "Saving..." : "Save"}
          </Button>
        </Box>
      </Paper>

      {mutation.isError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Error: {mutation.error.message}
        </Alert>
      )}

      {mutation.isPending && <LinearProgress sx={{ mb: 3 }} />}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          {/* Form Status */}
          <Box
            sx={{
              mb: 3,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip
                label={formData.active ? "Active" : "Inactive"}
                color={formData.active ? "success" : "default"}
                size="small"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.active}
                    onChange={handleChange}
                    name="active"
                    color="primary"
                  />
                }
                label={
                  formData.active ? "Visible to users" : "Hidden from users"
                }
              />
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {isEditMode
                ? "Editing existing podcast type"
                : "Creating new podcast type"}
            </Typography>
          </Box>

          <Divider sx={{ mb: 3 }} />

          <Box component="form" noValidate>
            <Grid container spacing={3}>
              {/* Title Field */}
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  id="title"
                  label="Podcast Type Title"
                  name="title"
                  autoFocus
                  value={formData.title}
                  onChange={handleChange}
                  error={!!errors.title}
                  helperText={
                    errors.title || `${charCount.title}/100 characters`
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <TitleIcon color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: formData.title ? (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setFormData((prev) => ({ ...prev, title: "" }));
                            setCharCount((prev) => ({ ...prev, title: 0 }));
                          }}
                          edge="end"
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  }}
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ ml: 1 }}
                >
                  This is the category name that will be displayed in the
                  podcast selection
                </Typography>
              </Grid>

              {/* Description Field */}
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  id="description"
                  label="Description"
                  name="description"
                  multiline
                  rows={4}
                  value={formData.description}
                  onChange={handleChange}
                  error={!!errors.description}
                  helperText={
                    errors.description ||
                    "Explain what kind of podcasts belong in this category"
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <DescriptionIcon color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: formData.description ? (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setFormData((prev) => ({
                              ...prev,
                              description: "",
                            }));
                            setCharCount((prev) => ({
                              ...prev,
                              description: 0,
                            }));
                          }}
                          edge="end"
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  }}
                />
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mt: 1,
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {charCount.description} characters
                  </Typography>
                  <Tooltip title="This description helps users understand what content to expect in this category">
                    <IconButton size="small">
                      <HelpIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </Card>

      {/* Bottom Action Buttons */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 3 }}>
        <Button
          variant="outlined"
          onClick={handleCancel}
          startIcon={<ArrowBackIcon />}
          disabled={mutation.isPending}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          startIcon={<SaveIcon />}
          disabled={mutation.isPending}
        >
          {mutation.isPending ? "Saving..." : isEditMode ? "Update" : "Create"}
        </Button>
      </Box>
    </Box>
  );
};

export default PodcastTypeForm;
